import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  CircularProgress, 
  Tabs, 
  Tab, 
  Button, 
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Explore as ExploreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import OutfitCard from '../Outfit/OutfitCard';
import InFeedAdGrid from '../Ads/InFeedAdGrid';
import { Outfit } from '../../types';
import outfitService from '../../services/outfit-service';
import followService from '../../services/follow-service';

interface FullUserFeedProps {
  feedType?: 'following' | 'discover' | 'trending';
  maxItems?: number;
  showFilters?: boolean;
  showTabs?: boolean;
}

interface FeedFilters {
  eventType?: string;
  sortBy: 'recent' | 'popular' | 'trending' | 'rating';
  timeframe?: 'day' | 'week' | 'month' | 'season' | 'all';
}

interface FeedStats {
  totalOutfits: number;
  followingCount: number;
  lastUpdated: Date;
}

/**
 * Map the UI sortBy filter value to the API sortBy parameter.
 * The backend /outfits endpoint accepts: 'newest', 'trending', 'rating'
 */
function mapSortByToApi(sortBy: string): string {
  switch (sortBy) {
    case 'popular': return 'trending';
    case 'trending': return 'trending';
    case 'rating': return 'rating';
    default: return 'newest';
  }
}

/**
 * Apply timeframe filter to an array of outfits client-side.
 */
function applyTimeframeFilter(items: Outfit[], timeframe?: string): Outfit[] {
  if (!timeframe || timeframe === 'all') return items;
  const now = new Date();
  const timeframeDays: Record<string, number> = { day: 1, week: 7, month: 30, season: 90 };
  const days = timeframeDays[timeframe] || 90;
  const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return items.filter(outfit => new Date(outfit.createdAt) >= cutoff);
}

const FullUserFeed: React.FC<FullUserFeedProps> = ({ 
  feedType: initialFeedType = 'discover',
  maxItems = 50,
  showFilters = true,
  showTabs = true
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  
  // State management
  const [feedType, setFeedType] = useState<'following' | 'discover' | 'trending'>(initialFeedType);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter and sort state
  const [filters, setFilters] = useState<FeedFilters>({
    sortBy: 'recent',
    timeframe: 'season'
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Feed statistics
  const [feedStats, setFeedStats] = useState<FeedStats>({
    totalOutfits: 0,
    followingCount: 0,
    lastUpdated: new Date()
  });
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Use refs for the followed user cache to avoid re-render loops.
  // State changes in useCallback deps cause the callback reference to change,
  // which causes loadFeed to change, which triggers the useEffect again.
  // Refs don't trigger re-renders, breaking the cycle.
  const followedIdsRef = useRef<Set<string>>(new Set());
  const followedUsersRef = useRef<any[]>([]);
  const followDataLoadedRef = useRef(false);

  /**
   * Load the list of user IDs the logged-in user follows.
   * Stores results in refs (not state) to avoid triggering re-renders.
   */
  const loadFollowedUsers = useCallback(async (): Promise<{ ids: string[]; users: any[] }> => {
    if (!user?.id) return { ids: [], users: [] };
    
    // Return cached data if already loaded this session
    if (followDataLoadedRef.current && followedIdsRef.current.size > 0) {
      return { 
        ids: Array.from(followedIdsRef.current), 
        users: followedUsersRef.current 
      };
    }
    
    try {
      console.log('🔄 Fetching following list for user:', user.id);
      const followingData = await followService.getFollowing(user.id, 500, 0);
      const users = followingData.users || [];
      const ids = users.map((u: any) => u.id);
      
      // Store in refs (no re-render)
      followedIdsRef.current = new Set(ids);
      followedUsersRef.current = users;
      followDataLoadedRef.current = true;
      
      console.log(`✅ Following list loaded: ${ids.length} users`);
      return { ids, users };
    } catch (err) {
      console.warn('Failed to load following list:', err);
      return { ids: [], users: [] };
    }
  }, [user?.id]);
  // Separate timeframe for the Following tab: 'all' shows everything, 'month' shows last 30 days
  const [followingTimeframe, setFollowingTimeframe] = useState<'all' | 'month'>('all');



  /**
   * Load following feed — outfits from users the logged-in user follows.
   * 
   * Strategy: 
   * 1. Get the follow list from Neptune via GET /users/{id}/following
   * 2. Fetch all active outfits from GET /outfits (with sorting, user enrichment, like status)
   * 3. Client-side filter to only outfits from followed users
   */
  const loadFollowingFeed = useCallback(async (): Promise<Outfit[]> => {
    if (!user?.id) throw new Error('User not authenticated');
    
    console.log('🔄 Loading following feed for user:', user.id);
    
    const { ids: followedIds, users: followedUsers } = await loadFollowedUsers();
    
    if (followedIds.length === 0) {
      console.log('ℹ️ User is not following anyone');
      return [];
    }

    // Fetch outfits from the /outfits endpoint with a larger limit
    // to have enough results after filtering to followed users only.
    const response = await outfitService.browseOutfits({
      limit: 100,
      sortBy: mapSortByToApi(filters.sortBy),
      page: 1,
      eventTypes: filters.eventType ? [filters.eventType] : undefined
    });

    const allOutfits: Outfit[] = response.outfits || [];
    const followedIdSet = new Set(followedIds);

    // Build a lookup map for followed user profile data
    const userMap = new Map<string, any>();
    followedUsers.forEach((u: any) => userMap.set(u.id, u));

    // Filter to only outfits from followed users
    let followingOutfits = allOutfits.filter(outfit => followedIdSet.has(outfit.userId));

    // Enrich with followed user profile data if the backend didn't already
    followingOutfits = followingOutfits.map(outfit => {
      const followedUser = userMap.get(outfit.userId);
      if (followedUser && !outfit.user) {
        return {
          ...outfit,
          user: {
            displayName: followedUser.displayName || followedUser.username,
            profilePicture: followedUser.profilePicture
          },
          userDisplayName: followedUser.displayName || followedUser.username,
          userProfilePicture: followedUser.profilePicture
        };
      }
      return outfit;
    });

    // Apply the Following tab's own timeframe selector
    followingOutfits = applyTimeframeFilter(followingOutfits, followingTimeframe);

    console.log(`✅ Following feed loaded: ${followingOutfits.length} outfits from ${followedIds.length} followed users`);
    return followingOutfits.slice(0, maxItems);
  }, [user?.id, filters.sortBy, filters.eventType, followingTimeframe, maxItems, loadFollowedUsers]);

  /**
   * Load discover feed — outfits from users the logged-in user does NOT follow.
   * 
   * Strategy:
   * 1. If authenticated, get the follow list to know who to exclude
   * 2. Fetch outfits from GET /outfits (already enriched with user data + like status)
   * 3. Client-side filter out outfits from followed users and the user's own outfits
   */
  const loadDiscoverFeed = useCallback(async (): Promise<Outfit[]> => {
    console.log('🔄 Loading discover feed with filters:', filters);

    // If authenticated, load followed user IDs to exclude them
    let excludeIds = followedIdsRef.current;
    if (isAuthenticated && user?.id) {
      const { ids } = await loadFollowedUsers();
      excludeIds = new Set(ids);
    }

    // Fetch outfits — request extra to compensate for filtering, but cap at 100
    const fetchLimit = Math.min(maxItems + 20, 100);

    const response = await outfitService.browseOutfits({
      limit: fetchLimit,
      sortBy: mapSortByToApi(filters.sortBy),
      page: 1,
      eventTypes: filters.eventType ? [filters.eventType] : undefined
    });

    let discoverOutfits: Outfit[] = response.outfits || [];

    // Exclude outfits from users the logged-in user already follows.
    // Discover is about finding new content from people you don't follow yet.
    // The user's own outfits are kept — they should see their content in the feed.
    if (isAuthenticated && user?.id && excludeIds.size > 0) {
      discoverOutfits = discoverOutfits.filter(outfit => {
        return !excludeIds.has(outfit.userId);
      });
    }

    // Apply timeframe filter
    discoverOutfits = applyTimeframeFilter(discoverOutfits, filters.timeframe);

    console.log(`✅ Discover feed loaded: ${discoverOutfits.length} outfits (excluded ${excludeIds.size} followed users + self)`);
    return discoverOutfits.slice(0, maxItems);
  }, [filters, maxItems, isAuthenticated, user?.id, loadFollowedUsers]);

  /**
   * Load trending feed — top-rated outfits from the current season (90 days).
   * 
   * Uses GET /outfits?sortBy=trending which sorts by trendingScore — a composite
   * of average rating, total ratings, likes, and recency with exponential time decay.
   * Trending is global and shows the best content regardless of follow status.
   * 
   * Applies a seasonal approach: only outfits from the last 90 days with an
   * average rating of 3.5 or higher qualify for the trending feed.
   */
  const loadTrendingFeed = useCallback(async (): Promise<Outfit[]> => {
    console.log('🔄 Loading trending feed with filters:', filters);

    // Fetch more than needed since we'll filter by rating client-side
    const fetchLimit = Math.min(maxItems * 2, 100);

    const response = await outfitService.browseOutfits({
      limit: fetchLimit,
      sortBy: 'trending',
      page: 1,
      eventTypes: filters.eventType ? [filters.eventType] : undefined
    });

    let trendingOutfits: Outfit[] = response.outfits || [];

    // Apply 90-day seasonal window for trending (always, regardless of user filter)
    const now = new Date();
    const seasonCutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    trendingOutfits = trendingOutfits.filter(outfit => new Date(outfit.createdAt) >= seasonCutoff);

    // Only show outfits rated 3.5 or higher — trending means quality content
    trendingOutfits = trendingOutfits.filter(outfit => {
      const avgRating = outfit.ratings?.averageRating || (outfit as any).averageRating || 0;
      return avgRating >= 3.5;
    });

    console.log(`✅ Trending feed loaded: ${trendingOutfits.length} outfits (90-day window, 3.5+ rating)`);
    return trendingOutfits.slice(0, maxItems);
  }, [filters, maxItems]);

  /**
   * Load feed data based on current feed type.
   * This is the main orchestrator — called by useEffect on mount and tab/filter changes.
   */
  const loadFeed = useCallback(async (refresh = false) => {
    if (!isAuthenticated && feedType === 'following') {
      setError('Please sign in to view your following feed');
      setLoading(false);
      return;
    }

    try {
      if (refresh) {
        setRefreshing(true);
        // Clear follow cache on refresh so we get fresh data
        followDataLoadedRef.current = false;
      } else {
        setLoading(true);
      }
      setError(null);
      setPage(1);
      
      let feedData: Outfit[] = [];
      
      switch (feedType) {
        case 'following':
          feedData = await loadFollowingFeed();
          break;
        case 'trending':
          feedData = await loadTrendingFeed();
          break;
        case 'discover':
        default:
          feedData = await loadDiscoverFeed();
          break;
      }
      
      setOutfits(feedData);
      setHasMore(feedData.length >= maxItems);
      
      setFeedStats({
        totalOutfits: feedData.length,
        followingCount: followedIdsRef.current.size,
        lastUpdated: new Date()
      });
      
    } catch (err: any) {
      console.error(`Error loading ${feedType} feed:`, err);
      setError(err.message || `Failed to load ${feedType} feed`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [feedType, isAuthenticated, loadFollowingFeed, loadTrendingFeed, loadDiscoverFeed, maxItems]);

  /**
   * Load more outfits for pagination (Load More button)
   */
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;

      const response = await outfitService.browseOutfits({
        limit: 20,
        sortBy: feedType === 'trending' ? 'trending' : mapSortByToApi(filters.sortBy),
        page: nextPage,
        eventTypes: filters.eventType ? [filters.eventType] : undefined
      });

      let newOutfits: Outfit[] = response.outfits || [];

      // Apply feed-specific filtering to the new page
      if (feedType === 'following' && followedIdsRef.current.size > 0) {
        newOutfits = newOutfits.filter(o => followedIdsRef.current.has(o.userId));
      } else if (feedType === 'discover' && isAuthenticated && user?.id) {
        newOutfits = newOutfits.filter(o => !followedIdsRef.current.has(o.userId));
      }

      // Apply timeframe filter
      newOutfits = applyTimeframeFilter(newOutfits, filters.timeframe);

      if (newOutfits.length === 0) {
        setHasMore(false);
      } else {
        setOutfits(prev => [...prev, ...newOutfits]);
        setPage(nextPage);
        setHasMore(response.pagination?.hasMore || newOutfits.length >= 20);
      }
    } catch (err) {
      console.error('Failed to load more outfits:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, filters, feedType, isAuthenticated, user?.id]);

  // Load feed when component mounts or when feedType/filters change
  useEffect(() => {
    if (!authLoading) {
      loadFeed();
    }
  }, [loadFeed, authLoading]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const feedTypes: ('following' | 'discover' | 'trending')[] = ['following', 'discover', 'trending'];
    setFeedType(feedTypes[newValue]);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<FeedFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setFilterMenuAnchor(null);
    setSortMenuAnchor(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadFeed(true);
  };

  // Get current tab index
  const getCurrentTabIndex = () => {
    const feedTypes = ['following', 'discover', 'trending'];
    return feedTypes.indexOf(feedType);
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading {feedType} feed...
        </Typography>
      </Box>
    );
  }

  // Authentication required for following feed
  if (!isAuthenticated && feedType === 'following') {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Please sign in to view your following feed
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Sign In
        </Button>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Feed Header */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1">
              {feedType === 'following' && <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
              {feedType === 'trending' && <TrendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
              {feedType === 'discover' && <ExploreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
              {feedType.charAt(0).toUpperCase() + feedType.slice(1)} Feed
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                title="Refresh feed"
              >
                <RefreshIcon />
              </IconButton>
              
              {showFilters && (
                <>
                  <IconButton 
                    onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                    title="Filter options"
                  >
                    <FilterIcon />
                  </IconButton>
                  
                  <IconButton 
                    onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                    title="Sort options"
                  >
                    <SortIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          
          {/* Feed Stats */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip 
              label={`${feedStats.totalOutfits} outfits`} 
              size="small" 
              variant="outlined" 
            />
            {feedType === 'following' && feedStats.followingCount > 0 && (
              <Chip 
                label={`Following ${feedStats.followingCount} users`} 
                size="small" 
                variant="outlined" 
              />
            )}
            <Chip 
              label={`Updated ${feedStats.lastUpdated.toLocaleTimeString()}`} 
              size="small" 
              variant="outlined" 
            />
          </Stack>
          
          {/* Active Filters */}
          {(filters.eventType || (filters.timeframe && filters.timeframe !== 'season')) && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Filters:
              </Typography>
              {filters.eventType && (
                <Chip 
                  label={`Event: ${filters.eventType}`}
                  size="small"
                  onDelete={() => handleFilterChange({ eventType: undefined })}
                />
              )}
              {filters.timeframe && filters.timeframe !== 'season' && (
                <Chip 
                  label={`Time: ${filters.timeframe}`}
                  size="small"
                  onDelete={() => handleFilterChange({ timeframe: 'season' })}
                />
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Feed Tabs */}
      {showTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={getCurrentTabIndex()} 
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab 
              icon={<PeopleIcon />} 
              label="Following" 
              disabled={!isAuthenticated}
            />
            <Tab 
              icon={<ExploreIcon />} 
              label="Discover" 
            />
            <Tab 
              icon={<TrendingIcon />} 
              label="Trending" 
            />
          </Tabs>
        </Box>
      )}

      {/* Following tab timeframe selector */}
      {feedType === 'following' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            value={followingTimeframe}
            exclusive
            onChange={(_, value) => { if (value) setFollowingTimeframe(value); }}
            size="small"
          >
            <ToggleButton value="all">All Outfits</ToggleButton>
            <ToggleButton value="month">Recent (30 days)</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      {/* Feed Content */}
      {outfits.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No outfits found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {feedType === 'following' 
                ? "Follow some users to see their outfits in your feed"
                : `No ${feedType} outfits match your current filters`
              }
            </Typography>
            {feedType === 'following' ? (
              <Button 
                variant="contained" 
                onClick={() => navigate('/discover')}
              >
                Discover Users
              </Button>
            ) : (
              <Button 
                variant="outlined" 
                onClick={() => setFilters({ sortBy: 'recent', timeframe: 'season' })}
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <InFeedAdGrid
            pageContext="home"
            gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          >
            {outfits.map((outfit, index) => (
              <OutfitCard 
                key={`${outfit.id}-${index}`}
                outfit={outfit} 
                showUserInfo={true}
              />
            ))}
          </InFeedAdGrid>
          
          {/* Load More Button */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={loadMore}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleFilterChange({ eventType: undefined })}>
          All Events
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ eventType: 'casual' })}>
          Casual
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ eventType: 'formal' })}>
          Formal
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ eventType: 'party' })}>
          Party
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ eventType: 'work' })}>
          Work
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleFilterChange({ timeframe: 'day' })}>
          Today
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ timeframe: 'week' })}>
          This Week
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ timeframe: 'month' })}>
          This Month
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ timeframe: 'season' })}>
          This Season (90 days)
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ timeframe: 'all' })}>
          All Time
        </MenuItem>
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleFilterChange({ sortBy: 'recent' })}>
          Most Recent
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ sortBy: 'popular' })}>
          Most Popular
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ sortBy: 'trending' })}>
          Trending
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ sortBy: 'rating' })}>
          Highest Rated
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FullUserFeed;
